import {gql} from '@apollo/client';
import React from 'react';

import {useBenchlingQuery, QueryResultType} from 'graphql-client/BenchlingQuery';
import {LibraryParameterDefinitionIdName} from 'procedures/parameter-library/ParameterLibraryTypes';
import type {
  LibraryParameterDefinitionSearchInputQuery,
  LibraryParameterDefinitionSearchInputQueryVariables,
} from 'procedures/parameter-library/ui/__generated/LibraryParameterDefinitionSearchInputQuery';
import {ChipSize} from 'ui-library/chips/Chip';
import ChipSelectInput from 'ui-library/inputs/ChipSelectInput';
import {OptionData} from 'ui-library/menus/MenuConstants';
import {getMatchingOptionDatas} from 'ui-library/menus/MenuUtil';
import {InputChangeEvent} from 'util/Types';

export const LIBRARY_PARAMETER_DEFINITION_SEARCH_INPUT_QUERY = gql`
  query LibraryParameterDefinitionSearchInputQuery($includeArchived: StrictBoolean)
  @skipAudit
  @engComponent(name: PROCEDURES) {
    allLibraryParameterDefinitions(includeArchived: $includeArchived) {
      id
      name
    }
  }
`;

export interface LibraryParameterDefinitionSearchInputProps {
  query: string;
  onQueryChange: (e: InputChangeEvent) => void;
  /** Invoked with id and name from the query when the user picks from the menu, or null when cleared. */
  onSelect: (definition: LibraryParameterDefinitionIdName | null) => void;
  selectedDefinition: LibraryParameterDefinitionIdName | null | undefined;
  onKeyDown?: (e: React.KeyboardEvent) => void;
}

/**
 * Typeahead input that searches library parameter definitions by name.
 */
export default function LibraryParameterDefinitionSearchInput({
  query,
  onQueryChange,
  onSelect,
  selectedDefinition,
  onKeyDown,
}: LibraryParameterDefinitionSearchInputProps): JSX.Element {
  const queryResult = useBenchlingQuery<
    LibraryParameterDefinitionSearchInputQuery,
    LibraryParameterDefinitionSearchInputQueryVariables
  >(LIBRARY_PARAMETER_DEFINITION_SEARCH_INPUT_QUERY, {
    variables: {includeArchived: false},
  });

  const definitions: Array<LibraryParameterDefinitionIdName> =
    queryResult.type !== QueryResultType.SUCCESS ? [] : queryResult.data.allLibraryParameterDefinitions;

  const allOptionDatas: Array<OptionData> = definitions.map((d) => ({
    name: d.name,
    value: d,
    pureTextName: d.name,
  }));
  const optionDatas = getMatchingOptionDatas(query, allOptionDatas);

  const value = selectedDefinition != null ? [selectedDefinition] : [];

  return (
    <ChipSelectInput
      placeholder=""
      optionDatas={value.length > 0 ? [] : optionDatas}
      query={query}
      value={value}
      onKeyDown={onKeyDown}
      onQueryChange={onQueryChange}
      scrollable={true}
      disableTyping={value.length > 0}
      getChipProps={(def: LibraryParameterDefinitionIdName) => ({
        text: def.name,
        size: ChipSize.TABLE_CELL,
        icon: 'parameters',
      })}
      onSelect={(selected: LibraryParameterDefinitionIdName | null): void => {
        onSelect(selected ?? null);
      }}
      onRemove={() => {
        onSelect(null);
      }}
    />
  );
}
